// src/pages/BlogPreview.jsx
import React, { useState, useEffect } from "react";
import { signIn } from 'aws-amplify/auth';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff } from "lucide-react";
import "../styles/Home.css";
import "../styles/BlogDetail.css";
import "../styles/Blog.css";
import Footer from '../components/Footer';
import { storeAccessToken } from '../utils/authUtils';

function BlogPreview() {
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [blogPosts, setBlogPosts] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    fetch('/blogPreviews.json')
      .then(response => response.json())
      .then(data => setBlogPosts(data))
      .catch(error => {
        console.error('Error loading blog posts:', error);
        setBlogPosts([]);
      });
  }, []);

  const handleCardClick = (post) => {
    setSelectedPost(post);
    setShowPreviewModal(true);
  };

  const handleLogin = async () => {
    setLoginError('');
    try {
      const user = await signIn({ username: email, password });
      await storeAccessToken();
      setShowPreviewModal(false);
      navigate('/dashboard/blog');
    } catch (err) {
      setLoginError("Login failed. Please check your email and password.");
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && email && password) {
      handleLogin();
    }
  };

  return (
    <div className="home-container" style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <header className="header header--scrolled">
        <div className="header__background" />
        <div className="header__content">
          <div className="header__branding">
            <a href="/">
              <img src="/images/skroop_white_transparent_thick.png" alt="Skroop Logo" className="header__logo" />
            </a>
            <a href="/"><span className="header__title">skroop</span></a>
          </div>
          <div className="header__actions">
            <button className="header__login" onClick={() => setShowPreviewModal(true)}>Login</button>
            <a href="/signup" className="header__cta">Try Skroop Free</a>
          </div>
        </div>
      </header>

      {/* Blog Preview Section */}
      <section className="blog-main-content" style={{ 
        padding: '8rem 2rem 4rem'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h1 style={{ 
            fontSize: '3rem', 
            fontWeight: '700', 
            marginBottom: '1rem', 
            color: '#111',
            textAlign: 'center'
          }}>
            Skroop Blog Preview
          </h1>
          <p style={{ 
            fontSize: '1.2rem', 
            lineHeight: '1.6', 
            color: '#555', 
            marginBottom: '3rem',
            textAlign: 'center',
            maxWidth: '600px',
            margin: '0 auto 3rem'
          }}>
            Thoughtful reflections on the metaphysics of modern morality.
          </p>
          
          <div className="blog-posts-grid">
            {blogPosts.length > 0 ? blogPosts.map(post => (
              <div 
                key={post.id}
                className="blog-card"
                onClick={() => handleCardClick(post)}
                style={{ cursor: 'pointer' }}
              >
                <img
                  src={post.heroImageUrl}
                  alt={post.title}
                />
                <div className="blog-content">
                  <div style={{
                    fontSize: '0.9rem',
                    color: '#6b7280',
                    marginBottom: '0.5rem'
                  }}>
                    {new Date(post.date).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </div>
                  <h2 style={{
                    fontSize: '1.25rem',
                    fontWeight: '700',
                    marginBottom: '0.5rem',
                    color: '#1a1a1a'
                  }}>
                    {post.title}
                  </h2>
                  <p className="blog-summary">
                    {post.excerpt}
                  </p>
                </div>
              </div>
            )) : (
              <div style={{ textAlign: 'center', padding: '2rem' }}>
                <p>Loading blog posts...</p>
              </div>
            )}
          </div>
          
          <div style={{ textAlign: 'center' }}>
            <a 
              href="/signup" 
              style={{
                backgroundColor: '#4f46e5',
                color: 'white',
                padding: '0.75rem 2rem',
                fontWeight: '600',
                fontSize: '1rem',
                textDecoration: 'none',
                borderRadius: '4px',
                display: 'inline-block'
              }}
            >
              Get Full Access
            </a>
          </div>
        </div>
      </section>

      <Footer />

      {/* Preview Modal with Screenshot Background */}
      {showPreviewModal && (
        <div className="modal-overlay" onClick={() => setShowPreviewModal(false)}>
          {/* Grayed out screenshot background */}
          {selectedPost && selectedPost.previewImageUrl && (
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              backgroundImage: `url(${selectedPost.previewImageUrl})`,
              backgroundSize: 'contain',
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'center',
              filter: 'brightness(0.7)',
              zIndex: 1
            }} />
          )}
          
          {/* Centered Login Modal */}
          <div className="login-modal" onClick={(e) => e.stopPropagation()} style={{ zIndex: 2, position: 'relative' }}>
            <button className="modal-close" onClick={() => setShowPreviewModal(false)}>×</button>
            <h2>Log in to Skroop</h2>
            {loginError && <div className="login-error" style={{color: 'red', marginBottom: '1rem'}}>{loginError}</div>}
            <input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={handleKeyDown}
              autoFocus
            />
            <div className="login-password-container">
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                onKeyDown={handleKeyDown}
                className="login-password-input"
              />
              <span
                className="password-toggle-icon"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </span>
            </div>
            <div className="forgot-password">
              <a href="/forgot">Forgot Password?</a>
            </div>
            <button type="button" className="login-button" onClick={handleLogin}>
              Log In
            </button>
            <p className="signup-link">
              Don't have an account? <a href="/signup">Sign up</a>
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export default BlogPreview;